/**
 * RegisterPage — 注册页（独立路由）
 * 视觉风格与登录页统一
 */
import { Typography, message } from 'antd';
import { Form, Input, Button } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { MailOutlined, LockOutlined, UserOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { useAuthStore } from '../../stores/auth';
import FireflyField from '../../components/FireflyField';

const { Text } = Typography;

export default function RegisterPage() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const register = useAuthStore((s) => s.register);
  const loading = useAuthStore((s) => s.loading);

  const onFinish = async (values: {
    email: string;
    password: string;
    nickname?: string;
  }) => {
    try {
      await register({
        email: values.email,
        password: values.password,
        nickname: values.nickname || values.email.split('@')[0],
      });
      message.success('注册成功！');
      navigate('/');
    } catch (err: any) {
      message.error(err?.message || '注册失败，请重试');
    }
  };

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* 背景 */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'url(/hero-deer-forest.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        zIndex: 0,
      }} />
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(90deg, rgba(4,15,34,0.38) 0%, rgba(4,15,34,0.22) 40%, rgba(4,15,34,0.08) 70%, transparent 100%)',
        zIndex: 1,
      }} />
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to bottom, transparent 60%, rgba(7,21,45,0.15) 80%, rgba(7,21,45,0.35) 100%)',
        zIndex: 1,
      }} />

      <FireflyField count={7} area="default" />

      {/* 注册面板 */}
      <div className="auth-register-panel" style={{
        position: 'relative',
        zIndex: 10,
        width: 440,
        maxWidth: '90vw',
        marginLeft: '8vw',
        padding: '44px 40px',
        background: 'rgba(8, 22, 40, 0.62)',
        borderRadius: 24,
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        border: '1px solid rgba(220,235,255,0.12)',
        boxShadow: '0 24px 80px rgba(0,0,0,0.25)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src="/logo-cat-white.png" alt="Simon Cat Agent" style={{ width: 40, height: 40, marginBottom: 16 }} />
          <div style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 20,
            fontWeight: 400,
            color: 'var(--text-primary)',
            marginBottom: 4,
          }}>
            加入萤火秘境
          </div>
          <Text style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
            开启 AI 创作之旅
          </Text>
        </div>

        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            label="邮箱"
            name="email"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入正确的邮箱格式' },
            ]}
          >
            <Input
              prefix={<MailOutlined style={{ color: 'rgba(220,235,255,0.42)' }} />}
              placeholder="请输入邮箱"
              style={{
                height: 52,
                background: 'rgba(255,255,255,0.045)',
                border: '1px solid rgba(220,235,255,0.10)',
                borderRadius: 14,
                color: 'var(--text-primary)',
                fontSize: 14,
              }}
            />
          </Form.Item>
          <Form.Item
            label="昵称"
            name="nickname"
            rules={[{ required: false }]}
          >
            <Input
              prefix={<UserOutlined style={{ color: 'rgba(220,235,255,0.42)' }} />}
              placeholder="选填，默认使用邮箱前缀"
              style={{
                height: 52,
                background: 'rgba(255,255,255,0.045)',
                border: '1px solid rgba(220,235,255,0.10)',
                borderRadius: 14,
                color: 'var(--text-primary)',
                fontSize: 14,
              }}
            />
          </Form.Item>
          <Form.Item
            label="密码"
            name="password"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码至少6位' },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: 'rgba(220,235,255,0.42)' }} />}
              placeholder="请输入密码"
              style={{
                height: 52,
                background: 'rgba(255,255,255,0.045)',
                border: '1px solid rgba(220,235,255,0.10)',
                borderRadius: 14,
                color: 'var(--text-primary)',
                fontSize: 14,
              }}
            />
          </Form.Item>
          <Form.Item
            label="确认密码"
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              { required: true, message: '请确认密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: 'rgba(220,235,255,0.42)' }} />}
              placeholder="请再次输入密码"
              style={{
                height: 52,
                background: 'rgba(255,255,255,0.045)',
                border: '1px solid rgba(220,235,255,0.10)',
                borderRadius: 14,
                color: 'var(--text-primary)',
                fontSize: 14,
              }}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={loading}
              style={{
                height: 52,
                background: 'linear-gradient(135deg, #F2D87E 0%, #DDB94F 100%)',
                border: 'none',
                borderRadius: 14,
                fontSize: 15,
                fontWeight: 600,
                color: '#08162A',
                boxShadow: '0 4px 20px rgba(235,216,126,0.18)',
              }}
            >
              加入萤火秘境 <ArrowRightOutlined style={{ fontSize: 12, marginLeft: 4 }} />
            </Button>
          </Form.Item>
          <div style={{ textAlign: 'center' }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: 13, fontFamily: 'var(--font-sans)' }}>
              已有账号？
            </span>
            <Link
              to="/auth/login"
              style={{ color: '#E9D38A', fontSize: 13, fontFamily: 'var(--font-sans)', opacity: 0.85 }}
            >
              立即登录
            </Link>
          </div>
        </Form>

        <div style={{
          textAlign: 'center',
          marginTop: 28,
          color: 'rgba(220,235,255,0.30)',
          fontSize: 12,
          fontFamily: 'var(--font-sans)',
        }}>
          © 2026 西蒙的猫 · 萤火秘境
        </div>
      </div>

      <style>{`
        .ant-input,
        .ant-input-password,
        .ant-input-affix-wrapper {
          background: rgba(255,255,255,0.045) !important;
          border-color: rgba(220,235,255,0.10) !important;
          color: var(--text-primary) !important;
        }
        .ant-input-password input,
        .ant-input-affix-wrapper input {
          background: transparent !important;
          color: var(--text-primary) !important;
        }
        .ant-input::placeholder { color: rgba(220,235,255,0.38) !important; }
        .ant-input:hover { border-color: rgba(220,235,255,0.18) !important; }
        .ant-input:focus, .ant-input-focused,
        .ant-input-password:focus-within,
        .ant-input-affix-wrapper-focused {
          border-color: rgba(235,220,155,0.45) !important;
          box-shadow: 0 0 0 3px rgba(235,220,155,0.06) !important;
        }
        .ant-form-item > .ant-form-item-label > label {
          color: rgba(220,235,255,0.65) !important;
          font-size: 13px !important;
        }
        .ant-form-item-explain-error {
          color: rgba(255,140,120,0.85) !important;
          font-size: 12px !important;
        }
        @media (max-width: 768px) {
          .auth-register-panel {
            margin-left: auto !important;
            margin-right: auto !important;
            padding: 36px 24px !important;
          }
        }
        @media (max-width: 480px) {
          .auth-register-panel {
            margin-left: 12px !important;
            margin-right: 12px !important;
            width: calc(100% - 24px) !important;
            max-width: 100% !important;
            padding: 28px 20px !important;
          }
        }
      `}</style>
    </div>
  );
}
